import React, { useCallback, useState } from 'react';
import { Clickable, Expandable, Input, Text, Stack } from '@moda/om';
import NextCircleIcon from '@moda/icons/next-circle-32';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useCountry } from '../CountrySelector';
import { PageFilterPanel } from './PageFilterPanel';
import './PageFilterPrice.scss';

type PageFilterPriceProps = {
  mobile?: boolean;
  onClose: () => void;
};

export const PageFilterPrice: React.FC<PageFilterPriceProps> = ({ mobile, onClose }) => {
  const { params, clearFilter, changePriceFilter } = usePLPParams();
  const { country } = useCountry();

  const [max, setMax] = useState(params?.pricemax?.toString() || '');
  const [min, setMin] = useState(params?.pricemin?.toString() || '');
  const [priceFilterError, setPriceFilterError] = useState('');

  const validatePriceFilter = useCallback(() => {
    if (Boolean(min) && Boolean(max) && Number(min) > Number(max)) {
      setPriceFilterError('Please ensure min is less than max.');
    } else {
      changePriceFilter(min, max);
      setPriceFilterError('');
    }
  }, [min, max, changePriceFilter]);

  const handleClear = useCallback(() => {
    clearFilter(['pricemin', 'pricemax']);
    setMin('');
    setMax('');
    setPriceFilterError('');
  }, [clearFilter, setMin, setMax]);

  const handleSubmit = useCallback(
    (evt: React.FormEvent) => {
      evt.preventDefault();
      validatePriceFilter();
    },
    [validatePriceFilter]
  );

  const content = (
    <form className="PageFilterPrice__form" onSubmit={handleSubmit}>
      <Stack space={2}>
        {priceFilterError && (
          <Text treatment="body2" color="code-red">
            {priceFilterError}
          </Text>
        )}
        <Stack direction="horizontal" space={2}>
          <Input
            className="PageFilterPrice__input"
            onChange={evt => setMin(evt.target.value)}
            label={`${country.currencyCode} min`}
            value={min}
            error={Boolean(priceFilterError)}
            maxLength={8}
          />
          <Text className="PageFilterPrice__text">to</Text>
          <Input
            className="PageFilterPrice__input"
            onChange={evt => setMax(evt.target.value)}
            label={`${country.currencyCode} max`}
            value={max}
            error={Boolean(priceFilterError)}
            maxLength={8}
          />
          <Clickable
            className="PageFilterPrice__submit"
            type="submit"
            aria-label="Submit price filters"
          >
            <NextCircleIcon />
          </Clickable>
        </Stack>
      </Stack>
    </form>
  );

  if (mobile) {
    return (
      <PageFilterPanel onClose={onClose} title="Price">
        {content}
      </PageFilterPanel>
    );
  }

  return (
    <Expandable className="PageFilterPrice" icon="chevron" name="Price">
      {content}
      <Clickable className="PageFilterPrice__clear" onClick={handleClear} type="button">
        Clear
      </Clickable>
    </Expandable>
  );
};
